import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Star, Quote } from "lucide-react";
import { REVIEWS_ALL, BUSINESS } from "@/data/content";
import useSEO from "@/hooks/useSEO";
import { breadcrumbSchema } from "@/lib/schema";
import { BUSINESS as BIZ } from "@/data/site";

const FILTERS = ["All", "EV Charger", "Panel Upgrade", "Commercial", "General", "Lighting", "Emergency"];

export default function ReviewsPage() {
  const [filter, setFilter] = useState("All");
  useSEO({
    title: "Reviews · 5.0★ · ElectroMax Systems | Metro Vancouver Electrician",
    description: "Read verified 5-star reviews from ElectroMax Systems customers across Metro Vancouver — EV charger installs, panel upgrades, commercial fit-outs, emergency service.",
    jsonld: [
      breadcrumbSchema([
        { name: "Home", href: "/" },
        { name: "Reviews", href: "/reviews" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": "https://electromax.ca/#business",
        name: "ElectroMax Systems",
        aggregateRating: BIZ.aggregateRating && {
          "@type": "AggregateRating",
          ratingValue: BIZ.aggregateRating.ratingValue,
          reviewCount: BIZ.aggregateRating.reviewCount,
          bestRating: 5,
          worstRating: 1,
        },
      },
    ],
  });

  const list = useMemo(() => {
    if (filter === "All") return REVIEWS_ALL;
    return REVIEWS_ALL.filter((r) => r.service?.toLowerCase() === filter.toLowerCase());
  }, [filter]);

  return (
    <div data-testid="reviews-page">
      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-16 overflow-hidden grain">
        <div className="pointer-events-none absolute -top-40 -right-20 w-[520px] h-[520px] bg-[#E60000]/10 blur-[120px] rounded-full" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 em-rise">
            <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4">— Reviews</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tighter font-black">
              320+ jobs. <br />
              <span className="text-white/40">One standard.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-white/70 leading-relaxed">
              Every review below is from a real Metro Vancouver customer. We don&rsquo;t filter, edit or reorder them. The scores are what they are.
            </p>
          </div>
          <div className="lg:col-span-4 em-rise" style={{ animationDelay: "120ms" }}>
            <div className="border border-[#262626] p-6 md:p-8 bg-[#141414]">
              <div className="flex items-center gap-2 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-[#E60000] fill-[#E60000]" strokeWidth={0} />
                ))}
              </div>
              <div className="font-display text-5xl font-black">5.0★</div>
              <div className="text-sm text-white/70 mt-2">Average across {REVIEWS_ALL.length}+ verified reviews</div>
              <a
                href="https://www.google.com/search?q=ElectroMax+Vancouver"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 border border-[#262626] hover:border-white text-white text-sm font-bold px-5 py-3 transition-colors"
              >
                Read on Google <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-y border-[#262626] bg-[#141414] py-4 sticky top-28 md:top-32 z-30 backdrop-blur">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 flex items-center gap-2 overflow-x-auto no-scrollbar">
          {FILTERS.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                type="button"
                data-testid={`review-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`shrink-0 text-xs tracking-[0.18em] uppercase font-bold px-4 py-2.5 border transition-colors ${
                  active
                    ? "bg-[#E60000] border-[#E60000] text-white"
                    : "bg-transparent border-[#262626] text-white/70 hover:text-white hover:border-white/40"
                }`}
              >
                {f}
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 md:py-24 bg-[#F4F4F5] text-[#0A0A0A]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          {list.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-black/20">
              <p className="text-black/60">No reviews in this category yet.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {list.map((r, i) => (
                <figure
                  key={i}
                  data-testid={`review-card-${i}`}
                  className="bg-white border border-[#E4E4E7] p-6 md:p-8 flex flex-col justify-between hover:border-black transition-colors"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex">
                        {Array.from({ length: r.stars }).map((_, j) => (
                          <Star key={j} className="w-3.5 h-3.5 text-[#E60000] fill-[#E60000]" strokeWidth={0} />
                        ))}
                      </div>
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[#E60000] font-bold">
                        {r.service}
                      </span>
                    </div>
                    <Quote className="w-5 h-5 text-black/30 mb-3" strokeWidth={2} />
                    <blockquote className="font-display text-lg leading-snug tracking-tight text-black">
                      &ldquo;{r.quote}&rdquo;
                    </blockquote>
                  </div>
                  <figcaption className="mt-6 pt-5 border-t border-[#E4E4E7]">
                    <div className="text-sm font-bold text-black">{r.name}</div>
                    <div className="text-xs text-black/55 mt-0.5">{r.role}</div>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-[#262626] bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tighter font-black leading-[0.95]">
              Want to be the next 5-star story?
            </h2>
            <p className="mt-3 text-white/70 max-w-xl">
              Get a free estimate — we&rsquo;ll be in touch within one business hour.
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end flex flex-wrap gap-3">
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-4 text-sm transition-colors"
            >
              Get a Free Quote <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href={BUSINESS.phoneHref}
              className="inline-flex items-center gap-2 border border-[#262626] hover:border-white text-white font-bold px-6 py-4 text-sm transition-colors"
            >
              {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
